/**
 * Invitations reçues (FND-V050-050, SRF-ACC-014) : les invitations encore utilisables de la
 * personne connectée, chacune avec l'espace, le rôle offert et son échéance, et un bouton
 * « Rejoindre ». Montée dans l'écran `/invitations`, dans la boîte d'attention et dans
 * l'accueil d'un porteur sans espace — là où l'invité regarde d'abord.
 *
 * La passerelle ne rend que SES invitations (organisation + e-mail du jeton) et exige un e-mail
 * attesté pour accepter sans le lien ; ce refus-là est dit tel quel, avec le recours (le lien).
 */
import type { JSX } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { MailOpen } from 'lucide-react'

import { api, type ApiError, type InvitationAcceptance } from '@/api/client'
import { ErrorState, LoadingState } from '@/components/ErrorState'
import { Button } from '@/components/ui/button'
import { useWorkspace } from '@/hooks/useWorkspace'
import { useT } from '@/i18n/language'

export const CLE_MES_INVITATIONS = ['me-invitations'] as const

const LIBELLE_ROLE: Record<string, string> = { owner: 'Propriétaire', contributor: 'Contributeur', viewer: 'Lecteur' }

/** Code d'erreur de la passerelle (`{"code": …}`), lu dans le corps conservé par `ApiError`. */
export function codeErreur(erreur: unknown): string | undefined {
  const detail = (erreur as ApiError | undefined)?.detail
  if (!detail) return undefined
  try { return (JSON.parse(detail) as { code?: string }).code } catch { return undefined }
}

/** Phrase de refus d'une acceptation, par code — jamais un « refusé » sans recours. */
export function messageRefusInvitation(erreur: unknown, t: ReturnType<typeof useT>['t']): string {
  switch (codeErreur(erreur)) {
    case 'INVITATION_CONSUMED': return t('invitations.refus.consommee', 'Cette invitation a déjà été acceptée.')
    case 'INVITATION_REVOKED': return t('invitations.refus.revoquee', 'Cette invitation a été révoquée par un propriétaire de l’espace ou par l’administration de l’organisation.')
    case 'INVITATION_EXPIRED': return t('invitations.refus.echue', 'Cette invitation a expiré : demandez-en une nouvelle au propriétaire de l’espace.')
    case 'INVITATION_EMAIL_UNVERIFIED': return t('invitations.refus.emailNonAtteste',
      'Votre adresse e-mail n’est pas attestée : ouvrez le lien d’invitation qui vous a été transmis pour rejoindre l’espace.')
    case 'INVITATION_UNAVAILABLE': return t('invitations.refus.indisponible',
      'Cette invitation n’existe pas ou n’est pas adressée au compte connecté (vérifiez l’adresse e-mail du compte).')
    default: return (erreur as Error | undefined)?.message ?? t('invitations.refus.inconnu', 'L’acceptation a échoué.')
  }
}

/** Après une acceptation : l'espace rejoint devient l'espace actif et sa Vue projet s'ouvre. */
export function useApresAcceptation(): (resultat: InvitationAcceptance) => void {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const espaces = useWorkspace()
  return (resultat) => {
    void queryClient.invalidateQueries({ queryKey: ['workspaces'] })
    void queryClient.invalidateQueries({ queryKey: CLE_MES_INVITATIONS })
    espaces.setActiveId?.(resultat.workspace_id)
    navigate('/projet')
  }
}

export function InvitationsRecues({ discret = false }: {
  /** Discret : rien n'est rendu tant qu'aucune invitation n'attend (boîte, accueil). */
  discret?: boolean
}): JSX.Element | null {
  const { t } = useT()
  const apres = useApresAcceptation()
  const liste = useQuery({ queryKey: CLE_MES_INVITATIONS, queryFn: api.listMyInvitations })
  const accepter = useMutation({
    mutationFn: (id: string) => api.acceptMyInvitation(id),
    onSuccess: apres,
    meta: { retour: 'toast', succes: t('invitations.rejointe', 'Espace rejoint') },
  })
  const invitations = liste.data ?? []
  if (discret && invitations.length === 0) return null
  return (
    <section className="space-y-3 rounded-lg border p-4" aria-labelledby="invitations-recues-titre" data-testid="invitations-recues">
      <h2 id="invitations-recues-titre" className="flex items-center gap-2 text-base font-semibold">
        <MailOpen className="h-4 w-4" aria-hidden="true" />
        {t('invitations.recues', 'Invitations reçues')}
      </h2>
      {liste.isPending ? <LoadingState /> : null}
      {liste.isError ? <ErrorState message={(liste.error as Error).message} onRetry={() => void liste.refetch()} /> : null}
      {liste.isSuccess && invitations.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="invitations-aucune">
          {t('invitations.aucune', 'Aucune invitation n’attend votre réponse. Un propriétaire d’espace peut vous en adresser une.')}
        </p>
      ) : null}
      {accepter.isError ? <p role="alert" className="text-sm text-destructive" data-testid="invitations-refus">{messageRefusInvitation(accepter.error, t)}</p> : null}
      {invitations.length > 0 ? (
        <ul className="space-y-2">
          {invitations.map((i) => (
            <li key={i.invitation_id} className="flex flex-wrap items-center justify-between gap-2 rounded border p-3" data-testid={`invitation-${i.invitation_id}`}>
              <span className="min-w-0 [overflow-wrap:anywhere]">
                <span className="block font-medium">{i.workspace_name}</span>
                <span className="block text-sm text-muted-foreground">
                  {t('invitations.detail', 'Rôle offert : {{role}}{{par}}{{echeance}}', {
                    role: LIBELLE_ROLE[i.role] ?? i.role,
                    par: i.invited_by ? t('invitations.par', ' · invité par {{nom}}', { nom: i.invited_by }) : '',
                    echeance: i.expires_at ? t('invitations.echeance', ' · valable jusqu’au {{date}}', { date: new Date(i.expires_at).toLocaleString() }) : '',
                  })}
                </span>
              </span>
              <Button type="button" size="sm" disabled={accepter.isPending} onClick={() => accepter.mutate(i.invitation_id)}
                      data-testid={`invitation-accepter-${i.invitation_id}`}>
                {t('invitations.rejoindre', 'Rejoindre l’espace')}
              </Button>
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  )
}
